// Pipeline-Board: eine Spalte je Status mit Zahl (und Jahreswert, wenn es Beträge gibt), Karten darunter, jede Spalte scrollt
// für sich und lädt am Ende nach. Karten lassen sich in eine andere Spalte ziehen (ein Zug = genau ein Statuswechsel).
import { useRef, useState, useEffect, type Dispatch, type DragEvent, type SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import type { OpportunityZeile, Board, BoardSpalte } from "../api/opportunities";
import { StatusPille } from "../components/Bausteine";
import { Icon } from "../components/Icon";
import { useMeldung } from "../components/Meldungen";
import { Karte, type KartenOptionen } from "./Karte";
import { spalteNachladen, type BoardParameter } from "./daten";
import { euro, statusFarbe } from "./logik";

interface Props {
  board: Board;
  setBoard: Dispatch<SetStateAction<Board | null>>;
  parameter: BoardParameter;
  optionen: KartenOptionen;
  darfAendern: (opp: OpportunityZeile) => boolean;
  onVerschieben: (opp: OpportunityZeile, statusId: string) => void;
  onBearbeiten: (opp: OpportunityZeile) => void;
  /** „+“ im Spaltenkopf: neue Opportunity in diesem Status */
  onNeu?: (statusId: string) => void;
  /** wie viele Karten eine Spalte jetzt zeigt (damit ein stilles Neuladen nicht zurückspringt) */
  onTiefe?: (anzahl: number) => void;
}

function NachladeAnker({ onSichtbar }: { onSichtbar: () => void }) {
  const el = useRef<HTMLDivElement>(null);
  const ref = useRef(onSichtbar);
  ref.current = onSichtbar;
  useEffect(() => {
    const knoten = el.current;
    if (!knoten) return;
    const beobachter = new IntersectionObserver(
      (e) => {
        if (e.some((x) => x.isIntersecting)) ref.current();
      },
      { rootMargin: "200px" },
    );
    beobachter.observe(knoten);
    return () => beobachter.disconnect();
  }, []);
  return <div ref={el} className="opp-spalte__anker" aria-hidden="true" />;
}

export function OpportunityBoard({ board, setBoard, parameter, optionen, darfAendern, onVerschieben, onBearbeiten, onNeu, onTiefe }: Props) {
  const navigate = useNavigate();
  const meldung = useMeldung();
  const [zieht, setZieht] = useState<OpportunityZeile | null>(null);
  const [ziel, setZiel] = useState<string | null>(null);
  const laedt = useRef(new Set<string>());
  const statuses = board.pipeline?.statuses ?? [];

  const oeffnen = (o: OpportunityZeile) => navigate(`/lead/${encodeURIComponent(o.lead_id)}`);

  const ziehStart = (e: DragEvent<HTMLElement>, opp: OpportunityZeile) => {
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", opp.id);
    } catch {
      /* manche Browser verlangen Daten, andere nicht */
    }
    setZieht(opp);
  };
  const ziehEnde = () => {
    setZieht(null);
    setZiel(null);
  };

  const nachladen = async (sp: BoardSpalte) => {
    const id = sp.status.id;
    if (!sp.cursor || laedt.current.has(id)) return;
    laedt.current.add(id);
    try {
      const r = await spalteNachladen(parameter, id, sp.cursor);
      setBoard((b) => {
        if (!b) return b;
        return {
          ...b,
          spalten: b.spalten.map((s) => {
            if (s.status.id !== id) return s;
            const bekannt = new Set(s.karten.map((k) => k.id));
            const karten = [...s.karten, ...r.karten.filter((k) => !bekannt.has(k.id))];
            return { ...s, karten, has_more: r.has_more, cursor: r.cursor };
          }),
        };
      });
      onTiefe?.(sp.karten.length + r.karten.length);
    } catch (e) {
      meldung.fehler(e, "Weitere Karten laden");
    } finally {
      laedt.current.delete(id);
    }
  };

  return (
    <div className="opp-board" role="list">
      {board.spalten.map((sp) => {
        const aufnehmen = !!zieht && zieht.status_id !== sp.status.id;
        return (
          <section
            key={sp.status.id}
            role="listitem"
            className={`opp-spalte${ziel === sp.status.id && aufnehmen ? " opp-spalte--ziel" : ""}`}
            aria-label={`${sp.status.label}, ${sp.anzahl} Opportunities`}
            onDragOver={(e) => {
              if (!aufnehmen) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              if (ziel !== sp.status.id) setZiel(sp.status.id);
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setZiel((z) => (z === sp.status.id ? null : z));
            }}
            onDrop={(e) => {
              if (!aufnehmen || !zieht) return;
              e.preventDefault();
              const karte = zieht;
              ziehEnde();
              onVerschieben(karte, sp.status.id);
            }}
          >
            <header className="opp-spalte__kopf">
              <StatusPille label={sp.status.label} farbe={statusFarbe(sp.status.type, sp.status.color)} />
              <span className="opp-spalte__rechts">
                {onNeu && sp.status.type === "active" ? (
                  <button type="button" className="symbolknopf symbolknopf--klein opp-spalte__plus" title={`Neue Opportunity: ${sp.status.label}`} aria-label={`Neue Opportunity im Status ${sp.status.label}`} onClick={() => onNeu(sp.status.id)}>
                    <Icon name="plus" groesse={14} />
                  </button>
                ) : null}
                <span className="opp-spalte__zahl" title={`${sp.anzahl} Opportunities`}>
                  {sp.anzahl.toLocaleString("de-DE")}
                </span>
              </span>
            </header>
            {sp.summe > 0 ? (
              <div className="opp-spalte__wert" title="Jahreswert: monatliche Beträge mal 12">
                <span>Jahreswert</span>
                <b>{euro(sp.summe)}</b>
              </div>
            ) : null}
            <div className="opp-spalte__karten">
              {sp.karten.map((k) => (
                <Karte
                  key={k.id}
                  opp={k}
                  statuses={statuses}
                  optionen={optionen}
                  darfAendern={darfAendern(k)}
                  zieht={zieht?.id === k.id}
                  onZiehStart={ziehStart}
                  onZiehEnde={ziehEnde}
                  onOeffnen={oeffnen}
                  onBearbeiten={onBearbeiten}
                  onStatus={onVerschieben}
                />
              ))}
              {sp.karten.length === 0 ? (
                <div className="opp-spalte__leer">{aufnehmen ? "Hierher ziehen" : "Keine Opportunities"}</div>
              ) : null}
              {sp.has_more ? <NachladeAnker onSichtbar={() => void nachladen(sp)} /> : null}
              {sp.has_more ? (
                <button type="button" className="knopf knopf--klein knopf--leise opp-spalte__mehr" onClick={() => void nachladen(sp)}>
                  Weitere laden ({(sp.anzahl - sp.karten.length).toLocaleString("de-DE")})
                </button>
              ) : null}
            </div>
          </section>
        );
      })}
    </div>
  );
}
